import { Skeleton, SkeletonPulse } from '../../components/Skeleton';
import { Platform } from '../../platform';
import { View } from '../../primitives/View';
import { cn } from '../../utils/cn';
import { StudyCardDivider, StudyCardFrame } from '../StudyCardFrame';

const IS_WEB = Platform.OS === 'web';

/**
 * - `tile`  — a card at rest in a deck: the deck pane, the expanded grid, the
 *   PDF / notes pane, the phone's card list and the iPad card navigator.
 * - `study` — the card a study session opens on.
 */
export type FlashcardSkeletonVariant = 'tile' | 'study';

/** The real card's header buttons: filled `secondary` chips, or bare `ghost` glyphs. */
export type FlashcardSkeletonActionVariant = 'secondary' | 'ghost';

export interface FlashcardSkeletonProps {
  variant?: FlashcardSkeletonVariant;
  /** The status badge at the start of the header row. Default true. */
  badge?: boolean;
  /**
   * How many action buttons the real card shows at rest in its header (edit,
   * delete, star…). Default 0 — e.g. a desktop deck card that reveals its
   * actions on hover.
   */
  actions?: number;
  /** Default: `ghost` for `study` on web (the session's corner buttons), else `secondary`. */
  actionVariant?: FlashcardSkeletonActionVariant;
  /** Pre-translated "Loading…" — announced once for the card. */
  accessibilityLabel?: string;
  /** Size of the card (the frame fills its parent by default). */
  className?: string;
  testID?: string;
}

/**
 * The real card's numbers, per surface — so the swap to the loaded card moves
 * nothing. Web (`FlashcardBorder`): `p-6` study / `p-4` deck, a 36px toolbar
 * row, the front taking what the back (`50% − 24px`) leaves, a `my-4` / `my-2`
 * divider. Native (`FlashcardListRow`, `AnimatedStudyCard`): `p-4` with the
 * corner badge row's `pt-14` clearance, two equal halves around a `my-3` (list)
 * / `my-4` (study) divider.
 */
const LAYOUT = {
  web: {
    tile: { pad: 'p-4', header: 'h-9', divider: 'my-2', line: 'h-4' },
    study: { pad: 'p-6', header: 'h-9', divider: 'my-4', line: 'h-5' },
  },
  native: {
    tile: { pad: 'p-4', header: 'mb-2 h-8', divider: 'my-3', line: 'h-4' },
    study: { pad: 'p-4', header: 'mb-2 h-8', divider: 'my-4', line: 'h-4' },
  },
} as const;

/** Web's back half is `50% − 24px` (the front grows into the rest); native's halves are equal. */
const BACK_SIZE = IS_WEB ? 'h-[calc(50%-24px)] shrink-0' : 'min-h-0 flex-1';

/**
 * Header buttons: `s` (36px) everywhere, except the phone list's `xs` (32px)
 * ghost glyphs, which sit with no gap (their hit boxes are the spacing).
 */
const ACTION_SLOT = {
  secondary: {
    slot: 'h-9 w-9 rounded-btn-s-outer-top',
    gap: IS_WEB ? 'gap-1.5' : 'gap-2',
  },
  ghost: { slot: IS_WEB ? 'h-9 w-9' : 'h-8 w-8', gap: IS_WEB ? 'gap-1.5' : '' },
} as const;

/** A question reads as a short line or two; an answer runs a little longer. */
const FRONT_LINES = ['w-3/4', 'w-1/2'];
const BACK_LINES = ['w-5/6', 'w-full', 'w-2/3'];

/**
 * The loading twin of a flashcard — ONE definition for every place a card is
 * still on its way, on both clients (there were six: different heights, icon
 * counts and sizes, line counts, divider positions).
 *
 * The frame is the real card's STILL surface (the shared study-card stroke,
 * fill and radius) with its divider where the real one sits; only the grey
 * shapes inside breathe, in one pulse — so it reads as a card, not a grey slab.
 * Header: the status badge (`m`, 32px; it carries its label in the web study
 * card) and the actions — filled chips, or a bare glyph for a ghost button.
 * Then centred question lines, the divider, centred answer lines.
 */
export function FlashcardSkeleton({
  variant = 'tile',
  badge = true,
  actions = 0,
  actionVariant = IS_WEB && variant === 'study' ? 'ghost' : 'secondary',
  accessibilityLabel,
  className,
  testID,
}: FlashcardSkeletonProps) {
  const layout = LAYOUT[IS_WEB ? 'web' : 'native'][variant];
  const action = ACTION_SLOT[actionVariant];

  return (
    <StudyCardFrame
      className={cn(
        'relative h-full w-full flex-col overflow-hidden',
        layout.pad,
        className,
      )}
      testID={testID}>
      {/* Still: the card's own divider, where the layout below puts it. */}
      <View className={layout.header} />
      <View className="min-h-0 flex-1" />
      <StudyCardDivider className={layout.divider} />
      <View className={BACK_SIZE} />

      <SkeletonPulse
        className={cn('absolute inset-0 flex-col', layout.pad)}
        accessibilityLabel={accessibilityLabel}>
        <View
          className={cn('w-full flex-row items-center justify-between', layout.header)}>
          {badge ? (
            <Skeleton
              className={cn(
                'h-8 rounded-[10px]',
                IS_WEB && variant === 'study' ? 'w-20' : 'w-8',
              )}
            />
          ) : (
            <View />
          )}
          <View className={cn('flex-row items-center', action.gap)}>
            {Array.from({ length: actions }, (_, i) =>
              actionVariant === 'ghost' ? (
                <View key={i} className={cn('items-center justify-center', action.slot)}>
                  <Skeleton className="h-4 w-4 rounded-[4px]" />
                </View>
              ) : (
                <Skeleton key={i} className={action.slot} />
              ),
            )}
          </View>
        </View>
        <Lines widths={FRONT_LINES} line={layout.line} />
        <View className={cn('h-px w-full', layout.divider)} />
        <View className={BACK_SIZE}>
          <Lines widths={BACK_LINES} line={layout.line} />
        </View>
      </SkeletonPulse>
    </StudyCardFrame>
  );
}
(FlashcardSkeleton as { displayName?: string }).displayName = 'FlashcardSkeleton';

/** Centred lines of text, the way a card face centres its content. */
function Lines({ widths, line }: { widths: string[]; line: string }) {
  return (
    <View className="min-h-0 w-full flex-1 items-center justify-center gap-2 px-4">
      {widths.map((width) => (
        <Skeleton key={width} shape="line" className={cn(line, width)} />
      ))}
    </View>
  );
}
